import { FunctionComponent } from "react";
import { isMobile } from "react-device-detect";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAppSelector } from "@/redux/hooks";
import { PUBLIC_PAGES_AFTER_LOGIN, ROUTE_CONFIG } from "@/routes/config";
import { AuthLayout, ErrorPage } from "@/components";
import { ErrorPageType } from "@/enum";

import { OnboardingLayout } from "@/components/Layouts/OnboardingLayout";

import { useIsFeatureActiveUnauthenticated } from "@/utils/useIsFeatureActiveUnauthenticated";
import { onboardingRedirectPath, resolveOnboardingVariant } from "@/utils/useOnboardingVariant";

import { NoMobilePage } from "@/components/NoMobilePage/NoMobilePage";

import { Login } from "./pages/Login/Login";
import { authSelector } from "./redux/auth.slice";
import { ForgotPassword } from "./pages/ForgotPassword/ForgotPassword";
import { SetPassword } from "./pages/SetPassword/SetPassword";
import { TwoFactorAuth } from "./pages/TwoFactorAuth/TwoFactorAuth";
import { ChangePassword } from "./pages/ChangePassword/ChangePassword";
import { Register } from "./pages/Register/Register";
import { FeaturesCodes } from "../Private/pages/Users/enum/codes";
import { ConfirmEmail } from "./pages/ConfirmEmail/ConfirmEmail";
import { VerifyEmail } from "./pages/VerifyEmail/VerifyEmail";
import { ApplicationStatus } from "./pages/ApplicationStatus/ApplicationStatus";
import { HubspotCallback } from "./pages/HubspotCallback/HubspotCallback";
import { ZohoCallback } from "./pages/ZohoCallback/ZohoCallback";
import { PipedriveCallback } from "./pages/PipedriveCallback/PipedriveCallback";
import { DynamicsCallback } from "./pages/DynamicsCallback/DynamicsCallback";
import { SalesforceCallback } from "./pages/SalesforceCallback/SalesforceCallback";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicy/PrivacyPolicyPage";
import { McpConsent } from "./pages/McpConsent/McpConsent";
import { takePendingConsentRoute } from "./pages/McpConsent/pendingConsent";

const { DASHBOARD, TFA, ONBOARDING, MCP_CONSENT } = ROUTE_CONFIG;

export const Public: FunctionComponent = () => {
	const { user, otpToken } = useAppSelector(authSelector);
	const { pathname } = useLocation();

	const SELF_REGISTER = useIsFeatureActiveUnauthenticated(FeaturesCodes.SELF_REGISTER);

	if (isMobile && pathname !== "/mobile-not-supported" && pathname !== "/register") {
		return <Navigate to="/mobile-not-supported" />;
	}

	const publicPages = PUBLIC_PAGES_AFTER_LOGIN.find((pp: string) => pathname.includes(pp));

	if (user && user.isOnboarded && !otpToken && !publicPages && pathname !== "/error-page") {
		// A user who was sent to log in from the MCP consent screen goes back there, not to the
		// dashboard — otherwise the authorization request is silently dropped.
		return <Navigate to={takePendingConsentRoute() ?? DASHBOARD} />;
	}

	if (user && !user.isOnboarded && !otpToken && !publicPages && pathname !== "/error-page") {
		return <Navigate to={onboardingRedirectPath(resolveOnboardingVariant(user)) ?? ONBOARDING} />;
	}

	if (user && otpToken && pathname !== TFA && pathname !== "/error-page") {
		return <Navigate to={TFA} />;
	}

	return (
		<Routes>
			<Route element={<Navigate to={SELF_REGISTER ? "/register" : "/login"} />} path="/" />
			<Route element={<OnboardingLayout />}>
				<Route element={<TwoFactorAuth />} path="2fa" />
				<Route element={<SetPassword />} path="/set-password" />
				<Route element={<ChangePassword />} path="/change-password" />
				<Route element={<ConfirmEmail />} path="/confirm-email" />
				<Route element={<VerifyEmail />} path="/verify-email" />
				<Route element={<ForgotPassword />} path="/forgot-password" />
			</Route>
			<Route element={<AuthLayout />}>
				<Route element={<Login />} path="login" />
				<Route element={<Register />} path="register" />
			</Route>
			<Route element={<HubspotCallback />} path="/hubspot/callback" />
			<Route element={<ZohoCallback />} path="/zoho/callback" />
			<Route element={<PipedriveCallback />} path="/pipedrive/callback" />
			<Route element={<DynamicsCallback />} path="/msdynamics/callback" />
			<Route element={<SalesforceCallback />} path="/salesforce/callback" />
			<Route element={<PrivacyPolicyPage />} path="/privacy-policy" />
			<Route element={<McpConsent />} path={MCP_CONSENT} />
			<Route element={<ErrorPage type={ErrorPageType.NotFound} />} path="*" />
			<Route element={<ErrorPage type={ErrorPageType.Forbidden} />} path="/error-page" />
			<Route
				element={<NoMobilePage type={ErrorPageType.MobileNotSupported} />}
				path="/mobile-not-supported"
			/>
		</Routes>
	);
};
